import { useQuery, QueryConfig } from 'react-query';
import { AxiosCustomError } from 'core/errors';
import { FlowOptions } from 'store/modules/main';
import { NOT_FOUND } from 'http-status-codes';
import {
  SdsInformation,
  SdsDataResearch,
  ScheduledAccountResponse,
} from 'features/ActivationClient/shared/Domicile';
import { ClientsAPI } from 'core/clients';
import { FilterParams } from 'features/ActivationClient/shared/Domicile/SearchDomicile';
import { DomicileAPI, ScheduledAccountParams } from './api';
import { ResearchSdsParams } from './DeliveryDomicile.model';

export function useGetRegions() {
  return useQuery(`domiciles/regions`, () => DomicileAPI.getRegions(), {
    refetchOnWindowFocus: false,
  });
}

export function useGetCities(region: string) {
  return useQuery(
    [`domiciles/cities`, region],
    (key, regionParam: string) =>
      DomicileAPI.getCities({ params: { region: regionParam } }),
    {
      enabled: region,
      refetchOnWindowFocus: false,
    }
  );
}

export function useGetSuburbs(region: string, city: string) {
  return useQuery(
    [`domiciles/suburbs`, region, city],
    (key, regionParam: string, cityParam: string) =>
      DomicileAPI.getSuburbs({
        params: { region: regionParam, city: cityParam },
      }),
    {
      enabled: region && city,
      refetchOnWindowFocus: false,
    }
  );
}

export function useGetFilteredAddresses(parameters?: FilterParams) {
  return useQuery(
    [`domiciles/filteredAddresses`, parameters],
    (key, params: FilterParams) => DomicileAPI.getFilteredAddresses({ params }),
    { enabled: parameters, retry: false }
  );
}

export function useScheduledAccount(
  parameters: ScheduledAccountParams,
  options: QueryConfig<ScheduledAccountResponse>
) {
  return useQuery(
    [`domiciles/scheduledAccount`, parameters],
    async (key, params: ScheduledAccountParams) => {
      try {
        return await DomicileAPI.getScheduledAccount({
          params: {
            domicile_id: params.domicile_id,
            business: params.business,
          },
        });
      } catch (e) {
        return {
          blockAccount: (e as AxiosCustomError)?.customError.code === '404',
          message: (e as AxiosCustomError)?.customError.description,
          status: (e as AxiosCustomError)?.customError.status,
          code: (e as AxiosCustomError)?.customError.code,
          severity: (e as AxiosCustomError)?.customError.severity,
          type: (e as AxiosCustomError)?.customError.type,
          transactionId: (e as AxiosCustomError)?.customError.transactionId,
        };
      }
    },
    { ...options, refetchOnWindowFocus: false }
  );
}
export function useResearchSds(
  parameters: ResearchSdsParams,
  options: QueryConfig<SdsDataResearch>
) {
  return useQuery(
    ['domiciles/sds', parameters],
    async (key, params: ResearchSdsParams) => {
      try {
        const { documentType, documentNumber } = params.clientDocument;

        const clientResponse = await ClientsAPI.getClientByAddress({
          params: {
            accountNumberId: params.accountNumberId,
            addressCity: params.addressCity,
            addressFlat: params.addressFlat,
            addressFloor: params.addressFloor,
            domicile_id: params.domicile_id,
            addressNumber: params.addressNumber,
            addressStreet: params.addressStreet,
            addressTower: params.addressTower,
            businessType: params.businessType,
            suburb: params.suburb,
            regionName: params.regionName,
          },
        }).catch((e: AxiosCustomError) => {
          // omit 404 errors, the endpoint could response "404" but is still a valid response
          if (
            e.customError.statusCode !== NOT_FOUND ||
            e.customError.type !== 'BUSINESS'
          ) {
            throw e;
          }
          return null;
        });

        const domicileResponse = await DomicileAPI.getSdSResearch({
          params: {
            account_id: params.accountNumberId ?? '',
            domicile_id: clientResponse?.addressAccountId ?? '',
            address_city: params.addressCity?.name ?? '',
            address_flat: params.addressFlat ?? '',
            address_floor: params.addressFloor ?? '',
            address_number: params.addressNumber ?? '',
            address_region: params.regionName?.name ?? '',
            address_street: params.addressStreet ?? '',
            business: params.businessType ?? '',
            suburb: params.suburb?.name ?? '',
            tower: params.addressTower ?? '',
          },
        });

        const sdsData = {
          listSds: [] as SdsInformation[],
          message: {
            status: domicileResponse.message.status,
            title: domicileResponse.message.messageTitle,
            subtitle: domicileResponse.message.messageSubTitle,
          },
        };

        const isDomicileUpgrade =
          params.flow === FlowOptions.SearchAddress && clientResponse;

        const addressMatchExisting =
          params.flow === FlowOptions.SearchDocument &&
          params.isNew &&
          params?.clientAddresses?.some(
            ({ id }) => id === clientResponse?.addressAccountId
          );

        const ownerMatch =
          params.flow === FlowOptions.SearchDocument &&
          documentNumber === clientResponse?.identificationNumber &&
          documentType === clientResponse?.identificationType;

        const domicileHasOwner = clientResponse?.identificationNumber;

        if (domicileHasOwner && !ownerMatch) {
          sdsData.message = {
            status: 'NOT OK',
            title: '¡Atención!',
            subtitle: 'El domicilio ya cuenta con una instalación',
          };
        } else if (addressMatchExisting) {
          sdsData.message = {
            status: 'NOT OK',
            title: '¡Atención!',
            subtitle:
              'El domicilio ya cuenta con una instalación, por favor, seleccionalo desde la lista de domicilios asociados',
          };
        } else if (isDomicileUpgrade) {
          sdsData.message = {
            status: 'NOT OK',
            title: '¡Atención!',
            subtitle:
              'El domicilio ya cuenta con una instalación, por favor, ingresá por Búsqueda por Documento',
          };
        }

        sdsData.listSds = domicileResponse.serviceRequests.map((value, idx) => {
          const data: SdsInformation = {
            sds: value.id,
            date: value.creationDate,
            status: value.description,
            motivo: 'Sin motivo',
            short_description: 'Sin motivo',
          };

          if (value.comment) {
            data.motivo = value.comment;
            data.short_description = value.comment.substring(0, 35);
          }
          return data;
        });

        return { ...sdsData, clientByAddress: clientResponse };
      } catch (e) {
        console.error(e);
        throw e;
      }
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
      cacheTime: 0,
      ...options,
    }
  );
}
